import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * People component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPeople extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M13.4201 6.88145C13.4201 9.45145 11.3401 11.5314 8.77012 11.5314C6.20012 11.5314 4.12012 9.45145 4.12012 6.88145C4.12012 4.31145 6.20012 2.23145 8.77012 2.23145C11.3401 2.23145 13.4201 4.31145 13.4201 6.88145ZM11.9201 6.88145C11.9201 5.14145 10.5101 3.73145 8.77012 3.73145C7.03012 3.73145 5.62012 5.14145 5.62012 6.88145C5.62012 8.62145 7.03012 10.0314 8.77012 10.0314C10.5101 10.0314 11.9201 8.62145 11.9201 6.88145ZM9.13 13.1515C10.44 13.2215 11.72 13.6115 12.84 14.3015V14.3115C13.96 15.0015 14.89 15.9715 15.54 17.1115C16.19 18.2515 16.54 19.5515 16.55 20.8615V21.7115C16.55 22.1215 16.21 22.4615 15.8 22.4615H1.75C1.34 22.4615 1 22.1215 1 21.7115V20.8615C1.01 19.5415 1.36 18.2415 2.01 17.1015C2.66 15.9615 3.59 14.9915 4.71 14.3015C5.82 13.6115 7.11 13.2115 8.42 13.1515C8.66 13.1415 8.89 13.1415 9.13 13.1515ZM2.5 20.9615H15.05V20.8615C15.04 19.8115 14.76 18.7615 14.24 17.8415C13.72 16.9215 12.96 16.1415 12.06 15.5815C11.16 15.0215 10.12 14.7015 9.06 14.6515H8.49C7.43 14.7015 6.39 15.0315 5.49 15.5815C4.58 16.1415 3.83 16.9215 3.31 17.8415C2.79 18.7615 2.51 19.8115 2.5 20.8715V20.9615ZM15.0195 10.0314C14.6095 10.0314 14.2695 10.3714 14.2695 10.7814C14.2695 11.1914 14.6095 11.5314 15.0195 11.5314C17.5895 11.5314 19.6695 9.45145 19.6695 6.88145C19.6695 4.31145 17.5895 2.23145 15.0195 2.23145C14.6095 2.23145 14.2695 2.57145 14.2695 2.98145C14.2695 3.39145 14.6095 3.73145 15.0195 3.73145C16.7595 3.73145 18.1695 5.14145 18.1695 6.88145C18.1695 8.62145 16.7595 10.0314 15.0195 10.0314ZM19.0796 14.3013C20.1996 14.9913 21.1296 15.9613 21.7796 17.1013C22.4296 18.2413 22.7796 19.5413 22.7896 20.8513V21.7013C22.7896 22.1113 22.4496 22.4513 22.0396 22.4513H18.9196C18.5096 22.4513 18.1696 22.1113 18.1696 21.7013C18.1696 21.2913 18.5096 20.9513 18.9196 20.9513H21.2896V20.8513C21.2696 19.8013 20.9896 18.7513 20.4696 17.8313C19.9496 16.9113 19.1896 16.1313 18.2896 15.5713C17.6996 15.2113 17.0696 14.9513 16.3996 14.8013C15.9996 14.7113 15.7496 14.3013 15.8396 13.9013C15.9296 13.5013 16.3296 13.2513 16.7396 13.3413C17.5696 13.5313 18.3596 13.8513 19.0796 14.3013Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-people') === undefined) {
  customElements.define('cat-icon-people', BlocksIconPeople);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-people': BlocksIconPeople;
  }
}
